// stores
import * as ContractStore from '../../../redux/middleware/ContractMiddleware';
import * as ContractStageStore from '../../../redux/middleware/ContractStageMiddleware';
import * as ContractConceptStore from '../../../redux/middleware/ContractConceptMiddleware';
import * as ProtocolsStore from '../../../redux/middleware/MedicalProtocolMiddleware';
import * as ProtocolConsumptionStore from '../../../redux/middleware/ProtocolConsumptionMiddleware';
import * as PractitionerStore from '../../../redux/middleware/PractitionerMiddleware';

// state
import { AppState } from '../../../redux/reducers';
import { Contract } from '../../../models/Entities/Contracts/Contract';
import { ContractStage } from '../../../models/Entities/Contracts/ContractStage';
import { ContractConcept } from '../../../models/Entities/Contracts/ContractConcept';
import { Protocol } from '../../../models/Entities/Protocols/Protocol';
import { ProtocolConsumption } from '../../../models/Entities/Protocols/ProtocolConsumption';
import { ProtocolPatient } from '../../../models/Entities/Protocols/ProtocolPatient';
import { PatientVisit } from '../../../models/Entities/Protocols/PatientVisist';
import { ErrorType } from '../../../models/HttpError';

//#region Props

interface IProps {
    ProtocolId: number;
}

interface ConnectedProps {
    Protocol: Protocol | null;
    Contract: Contract | null;
    Stages: ContractStage[];
    Concepts: ContractConcept[];
    Consumptions: ProtocolConsumption[];
    Patients: ProtocolPatient[];
    PatientVisits: PatientVisit[];
    IsLoading: boolean;
    Error: ErrorType | undefined;
    AccessToken: string | undefined;
}

interface DispatchProps {
    GetMedicalProtocolById: typeof ProtocolsStore.actionCreators.GetMedicalProtocolById;
    GetProtocolPatients: typeof ProtocolsStore.actionCreators.GetProtocolPatients;
    GetPatientVisitByProtocolId: typeof ProtocolsStore.actionCreators.GetPatientVisitByProtocolId;
    GetAllProtocolContractAssociations: typeof ProtocolsStore.actionCreators.GetAllProtocolContractAssociations;
    GetContractById: typeof ContractStore.actionCreators.GetContractById;
    GetAllContractsStage: typeof ContractStageStore.actionCreators.GetAllContractsStage;
    GetAllContractsConcept: typeof ContractConceptStore.actionCreators.GetAllContractsConcept;
    GetAllProtocolConsumptionsByProtocol: typeof ProtocolConsumptionStore.ActionCreators.GetAllProtocolConsumptionsByProtocol;
    GetAllPractitioners: typeof PractitionerStore.actionCreators.GetAllPractitioners;
}

export type Props = IProps & ConnectedProps & DispatchProps;

export const mapStateToProps = (state: AppState, ownProps: IProps) => {
    const protocol: Protocol | null =
        state.MedicalProtocols?.list?.find((p: Protocol) => p.id === ownProps.ProtocolId) ?? null;

    const contractId = protocol?.contractAssociation?.contractId ?? null;
    const contract: Contract | null =
        contractId
            ? state.Contract?.list?.find((c: Contract) => c.id === contractId) ?? null
            : null;

    const stages: ContractStage[] = contract?.stages ?? [];
    const concepts: ContractConcept[] = contract?.concepts ?? [];

    return {
        Protocol: protocol,
        Contract: contract,
        Stages: stages,
        Concepts: concepts,
        Consumptions: protocol?.consumptions ?? [],
        Patients: protocol?.patients ?? [],
        PatientVisits: protocol?.patientVisits ?? [],
        IsLoading:
            state.MedicalProtocols?.isLoadingOne ||
            state.Contract?.isLoadingOne ||
            false,
        Error: state.MedicalProtocols?.error ?? undefined,
        AccessToken: (state as any)?.oidc?.user?.access_token as string | undefined,
    };
};

export const mapDispatchToProps = {
    ...ContractStore.actionCreators,
    ...ContractStageStore.actionCreators,
    ...ContractConceptStore.actionCreators,
    ...ProtocolsStore.actionCreators,
    ...ProtocolConsumptionStore.ActionCreators,
    ...PractitionerStore.actionCreators,
};

//#endregion

// ——— Pure computation helpers ———

export interface PatientSummary {
    numberInProtocol: string;
    totalAmount: number;
    settledAmount: number;
    pendingAmount: number;
    consumptionCount: number;
    visitsDone: number;
    currency: string;
}

export interface StageSummary {
    stageId: number;
    stageName: string;
    visitsDone: number;
    totalAmount: number;
    pendingAmount: number;
    currency: string;
}

export interface ConceptSummary {
    conceptName: string;
    total: number;
    settled: number;
    pending: number;
    currency: string;
}

/** Consumptions that count as "settled" (status 1, 2, 3 or 6) */
const SETTLED_STATUSES = new Set([1, 2, 3, 6]);

export const buildPatientSummaries = (
    consumptions: ProtocolConsumption[],
    patients: ProtocolPatient[]
): PatientSummary[] => {
    const map = new Map<string, PatientSummary>();

    patients.forEach(p => {
        map.set(p.numberInProtocol, {
            numberInProtocol: p.numberInProtocol,
            totalAmount: 0,
            settledAmount: 0,
            pendingAmount: 0,
            consumptionCount: 0,
            visitsDone: 0,
            currency: '',
        });
    });

    consumptions.forEach(c => {
        const key = c.patientNumberInProtocol;
        if (!key) return;
        if (!map.has(key)) {
            map.set(key, {
                numberInProtocol: key,
                totalAmount: 0,
                settledAmount: 0,
                pendingAmount: 0,
                consumptionCount: 0,
                visitsDone: 0,
                currency: c.currencyCode ?? '',
            });
        }
        const s = map.get(key);
        if (!s) return;
        s.totalAmount += c.totalAmount ?? 0;
        s.pendingAmount += c.pendingAmount ?? 0;
        s.currency = s.currency || (c.currencyCode ?? '');
        s.consumptionCount += 1;
        if (SETTLED_STATUSES.has(c.status)) {
            s.settledAmount += c.totalAmount ?? 0;
        }
    });

    return Array.from(map.values()).sort((a, b) =>
        a.numberInProtocol.localeCompare(b.numberInProtocol)
    );
};

export const buildStageSummaries = (
    consumptions: ProtocolConsumption[],
    stages: ContractStage[]
): StageSummary[] => {
    const map = new Map<number, StageSummary>();

    stages.forEach(s => {
        map.set(s.id, {
            stageId: s.id,
            stageName: s.stageName,
            visitsDone: 0,
            totalAmount: 0,
            pendingAmount: 0,
            currency: '',
        });
    });

    consumptions.forEach(c => {
        if (!c.stageId) return;
        if (!map.has(c.stageId)) {
            map.set(c.stageId, {
                stageId: c.stageId,
                stageName: c.stageName ?? `Visita ${c.stageId}`,
                visitsDone: 0,
                totalAmount: 0,
                pendingAmount: 0,
                currency: c.currencyCode ?? '',
            });
        }
        const s = map.get(c.stageId);
        if (!s) return;
        s.totalAmount += c.totalAmount ?? 0;
        s.pendingAmount += c.pendingAmount ?? 0;
        s.currency = s.currency || (c.currencyCode ?? '');
    });

    // count unique patient×stage pairs
    const visitPairs = new Set<string>();
    consumptions.forEach(c => {
        if (c.stageId && c.patientNumberInProtocol)
            visitPairs.add(`${c.stageId}::${c.patientNumberInProtocol}`);
    });
    visitPairs.forEach(pair => {
        const stageId = Number(pair.split('::')[0]);
        const s = map.get(stageId);
        if (s) s.visitsDone += 1;
    });

    return Array.from(map.values()).sort((a, b) => a.stageName.localeCompare(b.stageName));
};

export const buildConceptSummaries = (
    consumptions: ProtocolConsumption[]
): ConceptSummary[] => {
    const map = new Map<string, ConceptSummary>();

    consumptions.forEach(c => {
        const name = c.serviceName ?? 'Sin concepto';
        if (!map.has(name)) {
            map.set(name, { conceptName: name, total: 0, settled: 0, pending: 0, currency: c.currencyCode ?? '' });
        }
        const s = map.get(name);
        if (!s) return;
        s.total += c.totalAmount ?? 0;
        s.pending += c.pendingAmount ?? 0;
        s.currency = s.currency || (c.currencyCode ?? '');
        if (SETTLED_STATUSES.has(c.status)) s.settled += c.totalAmount ?? 0;
    });

    return Array.from(map.values())
        .sort((a, b) => b.total - a.total)
        .slice(0, 15);
};

export const fmt = (amount: number, currency: string) => {
    const curr = (currency ?? 'ARS').toUpperCase();
    try {
        return amount.toLocaleString('es-AR', { style: 'currency', currency: curr });
    } catch {
        return `${curr} ${amount.toFixed(2)}`;
    }
};
